import { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, Link } from 'react-router-dom';
import Logo from './Logo';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 15);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu & dropdown on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Click outside listener for services dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setServicesDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { label: 'Forside', path: '/' },
    {
      label: 'Ydelser',
      path: '/ydelser',
      subItems: [
        { label: 'Klinisk Videoproduktion', path: '/ydelser/klinisk-videoproduktion' },
        { label: 'Shortform Content', path: '/ydelser/shortform-content' },
        { label: 'Distribution & Vækst', path: '/ydelser/distribution-og-synlighed' }
      ]
    },
    { label: 'Cases', path: '/cases' },
    { label: 'Om os', path: '/om-os' },
    { label: 'Kontakt', path: '/kontakt' }
  ];

  const isServicesActive = 
    location.pathname.startsWith('/ydelser') || 
    location.pathname.startsWith('/shortform-content') ||
    location.pathname.startsWith('/klinisk-videoproduktion') ||
    location.pathname.startsWith('/distribution-og-synlighed');

  return (
    <>
      <header 
        className={`sticky top-0 z-40 w-full bg-white transition-[border-color,box-shadow] duration-200 border-b py-3.5 sm:py-4 ${
          scrolled 
            ? 'border-slate-200 shadow-xs' 
            : 'border-slate-100'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            
            {/* Logo / Branding */}
            <Link 
              to="/" 
              className="flex items-center group focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 rounded-lg"
              aria-label="Health4media.dk - Gå til forsiden"
            >
              <Logo variant="default" size="md" />
            </Link>

            {/* Desktop Navigation (Visible only at >= 1200px) */}
            <nav 
              className="hidden min-[1200px]:flex items-center gap-2"
              aria-label="Hovednavigation"
            >
              {navLinks.map((link) => {
                if (link.subItems) {
                  return (
                    <div 
                      key={link.path} 
                      className="relative" 
                      ref={dropdownRef}
                      onMouseEnter={() => setServicesDropdownOpen(true)}
                      onMouseLeave={() => setServicesDropdownOpen(false)}
                    >
                      <button
                        type="button"
                        onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
                        className={`inline-flex items-center gap-2 px-3 py-2 text-base font-medium transition-colors cursor-pointer ${
                          isServicesActive
                            ? 'text-black font-semibold'
                            : 'text-slate-700 hover:text-black'
                        }`}
                        aria-expanded={servicesDropdownOpen}
                        aria-haspopup="true"
                      >
                        <span>{link.label}</span>
                        <i 
                          className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                            servicesDropdownOpen ? 'rotate-180 text-black' : isServicesActive ? 'text-black' : 'text-slate-400'
                          }`} 
                          aria-hidden="true" 
                        />
                      </button>

                      {/* Dropdown Menu */}
                      {servicesDropdownOpen && (
                        <div className="absolute left-0 top-full pt-2 w-72 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                          <div className="bg-white rounded-xl shadow-xl border border-slate-200/80 p-2 ring-1 ring-slate-950/5">
                            {link.subItems.map((sub) => (
                              <Link
                                key={sub.path}
                                to={sub.path}
                                onClick={() => setServicesDropdownOpen(false)}
                                className="flex items-center px-3.5 py-2.5 rounded-lg hover:bg-slate-50 transition group"
                              >
                                <span className={`text-[15px] ${location.pathname === sub.path ? 'font-bold text-black' : 'font-semibold text-slate-900 group-hover:text-black'}`}>
                                  {sub.label}
                                </span>
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    end={link.path === '/'}
                    className={({ isActive }) =>
                      `px-3 py-2 text-base font-medium transition-colors ${
                        isActive
                          ? 'text-black font-semibold'
                          : 'text-slate-700 hover:text-black'
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                );
              })}
            </nav>

            {/* Desktop Right CTA (Visible at >= 1200px) */}
            <div className="hidden min-[1200px]:flex items-center gap-3">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-slate-900 text-white hover:bg-sky-700 font-semibold text-[15px] transition-all duration-200 shadow-xs hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
              >
                <span>Book en samtale</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>

            {/* Burger Menu Button (Visible strictly below 1200px) */}
            <div className="flex min-[1200px]:hidden items-center gap-2">
              <Link
                to="/kontakt"
                className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-slate-900 text-white text-xs font-semibold hover:bg-sky-700 transition shadow-xs"
              >
                <span>Book samtale</span>
              </Link>

              <button
                type="button"
                onClick={() => setMobileMenuOpen(true)}
                className="p-1.5 text-slate-800 focus:outline-none cursor-pointer"
                aria-label="Åbn menu"
                aria-expanded={mobileMenuOpen}
              >
                <i className="fa-solid fa-bars text-2xl" aria-hidden="true" />
              </button>
            </div>

          </div>
        </div>
      </header>

      {/* Mobile Off-Canvas Sidebar Menu */}
      <div 
        className={`min-[1200px]:hidden fixed inset-0 z-50 transition-visibility duration-300 ${
          mobileMenuOpen ? 'visible pointer-events-auto' : 'invisible pointer-events-none'
        }`}
        aria-hidden={!mobileMenuOpen}
      >
        {/* Backdrop Overlay */}
        <div 
          onClick={() => setMobileMenuOpen(false)}
          className={`fixed inset-0 bg-slate-950/60 backdrop-blur-xs transition-opacity duration-300 ease-in-out ${
            mobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
        />

        {/* Sidebar Drawer Panel */}
        <aside 
          className={`fixed top-0 bottom-0 right-0 w-[310px] sm:w-[360px] max-w-[88vw] bg-white z-50 shadow-2xl flex flex-col justify-between overflow-y-auto transition-transform duration-300 ease-out transform ${
            mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          aria-label="Mobil navigation"
        >
          {/* Sidebar Top Header with Logo & Close Button */}
          <div>
            <div className="flex items-center justify-between px-5 py-4.5 border-b border-slate-100 bg-white sticky top-0 z-10">
              <Link 
                to="/" 
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center"
                aria-label="Health4media.dk - Forside"
              >
                <Logo variant="default" size="sm" />
              </Link>

              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full text-slate-500 hover:text-black hover:bg-slate-100 transition cursor-pointer"
                aria-label="Luk menu"
              >
                <i className="fa-solid fa-xmark text-xl" aria-hidden="true" />
              </button>
            </div>

            {/* Navigation Links */}
            <nav className="p-4 space-y-1">
              {navLinks.map((link) => {
                if (link.subItems) {
                  return (
                    <div key={link.path} className="py-1">
                      <div className="flex items-center justify-between">
                        <NavLink
                          to={link.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={({ isActive }) =>
                            `flex-1 py-2.5 px-3 rounded-lg text-base font-semibold transition ${
                              isActive || isServicesActive
                                ? 'text-black font-bold bg-slate-50'
                                : 'text-slate-800 hover:text-black'
                            }`
                          }
                        >
                          {link.label}
                        </NavLink>
                        <button
                          type="button"
                          onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                          className="p-2.5 text-slate-500 hover:text-black transition"
                          aria-label="Vis undersider"
                        >
                          <i 
                            className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                              mobileServicesOpen ? 'rotate-180 text-black' : ''
                            }`} 
                            aria-hidden="true" 
                          />
                        </button>
                      </div>

                      {mobileServicesOpen && (
                        <div className="pl-3 mt-1 space-y-1 border-l-2 border-slate-100 ml-3">
                          {link.subItems.map((sub) => (
                            <Link
                              key={sub.path}
                              to={sub.path}
                              onClick={() => setMobileMenuOpen(false)}
                              className={`block px-3 py-2 rounded-md text-sm transition ${
                                location.pathname === sub.path
                                  ? 'text-black font-bold bg-slate-100'
                                  : 'text-slate-700 font-medium hover:text-black hover:bg-slate-50'
                              }`}
                            >
                              {sub.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    end={link.path === '/'}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center justify-between px-3 py-3 rounded-lg text-base font-semibold transition ${
                        isActive
                          ? 'text-black font-bold bg-slate-50'
                          : 'text-slate-800 hover:text-black hover:bg-slate-50'
                      }`
                    }
                  >
                    <span>{link.label}</span>
                    <i className="fa-solid fa-chevron-right text-xs text-slate-300" aria-hidden="true" />
                  </NavLink>
                );
              })}
            </nav>
          </div>

          {/* Sidebar Bottom Action & Contact Info */}
          <div className="p-5 border-t border-slate-100 bg-slate-50/50 space-y-4">
            <Link
              to="/kontakt"
              onClick={() => setMobileMenuOpen(false)}
              className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-full bg-slate-900 text-white hover:bg-sky-700 font-semibold text-sm text-center transition shadow-sm"
            >
              <span>Book en samtale</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>

            <div className="text-xs text-slate-500 space-y-1.5 pt-1 text-center">
              <p>
                <a href="tel:61489206" className="hover:text-black transition font-medium">
                  <i className="fa-solid fa-phone mr-1.5 text-sky-600" aria-hidden="true" />
                  61 48 92 06
                </a>
              </p>
              <p>
                <a href="mailto:health4media@gmail.com" className="hover:text-black transition">
                  <i className="fa-solid fa-envelope mr-1.5 text-sky-600" aria-hidden="true" />
                  health4media@gmail.com
                </a>
              </p>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
}
